'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');

/**
 * @typedef {{
 *     accessToken: string,
 *     availableProducts: string,
 *     partner: string,
 *     productType: string,
 *     uuid: string,
 * }} UnitType
 *
 * @callback MessageCallback
 * @param {MessageEvent} event
 */

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * PhoenixEvenFinancial
     *
     * A webcomponent that loads a finance partner embed.
     *
     * @example
     * <phoenix-even-financial />
     */
    class PhoenixEvenFinancial extends pageClass.PhoenixElement {
        /** @type {MessageCallback | undefined} */
        #fionaListener = undefined;

        connectedCallback() {
            /** @type {Record<string, UnitType>} */
            const unitTypes = {
                loan: {
                    accessToken: '149e0e7b-b214-4fdd-8dca-f6bd5e015663_03725c9e-1b7a-485a-be3d-fed61a7f36b8',
                    availableProducts: 'loan',
                    partner: 'street-embed-loans',
                    productType: 'loan',
                    uuid: 'acce1978-ba95-434f-9cd1-ddb35aa62752',
                },
                loanMarketplace: {
                    accessToken: 'fb9c394e-ea13-4ad5-8629-fb9bf13369fe_b4081fee-e94c-4b38-8f74-e216a3ca29c4',
                    availableProducts: 'loan',
                    partner: 'street-marketplace-loans',
                    productType: 'loan',
                    uuid: '4a3044ee-2448-4aa0-812d-eef2c8d58fa9',
                },
                savings: {
                    accessToken: '992ff78f-b0b3-4023-90e1-bb5c5064cbc0_af465eb6-2335-4ba7-b989-8d85104e0d00',
                    availableProducts: 'deposits',
                    partner: 'street-embed-savings',
                    productType: 'savings',
                    uuid: 'bfeee389-51ab-4cd7-bfcc-e1ce559642fe',
                },
                savingsMarketplace: {
                    accessToken: '8fbae3e6-4aa8-4b38-91bd-4f7227251c5e_a7049b40-5b8a-45f1-b44a-f5f301342587',
                    availableProducts: 'deposits',
                    partner: 'street-marketplace-savings',
                    productType: 'savings',
                    uuid: '5fbb5942-9626-4381-9c19-41fa2ecbe4bf',
                },
            };

            const unitType = unitTypes[this.config?.unitType] || unitTypes['loan'];
            this.canRender().then((canRender) => {
                if (canRender) {
                    this.#trackToPermutive(unitType);
                    pageClass.util.addScriptResourceOnce(
                        'https://embed.hifiona.com/script/helper/bundle.js',
                        { id: 'hifiona-platform' },
                        document.body
                    );
                    this.innerHTML = `<iframe class="even-iframe" id="iFrameResizer0" scrolling="no" src="https://embed.hifiona.com/ui/multi-product/index.html?partner=${unitType.partner}&access_token=${unitType.accessToken}&company_uuid=${unitType.uuid}&productType=${unitType.productType}&pw_fiona=true&available_products=${unitType.availableProducts}" style="width: 100%; border: none;"></iframe>`;
                } else {
                    this.innerHTML = `<div class="mm-custom-element--fallback"></div>`;
                }
            });
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            if (this.#fionaListener) {
                window.removeEventListener('message', this.#fionaListener);
            }
        }

        /**
         * @param {UnitType} unitType
         */
        #trackToPermutive(unitType) {
            if (!window.permutive) {
                return;
            }

            this.#fionaListener = (event) => {
                if (!`${event.data.type}`.startsWith('fiona')) {
                    return;
                }

                const eventType = event.data.type.replace('fiona_', '');
                if (this.#isObservedEvent(eventType)) {
                    window.permutive.track('Tracking', {
                        type: `fiona_${unitType.availableProducts}`,
                        data: {
                            action: eventType,
                        },
                    });
                }
            };

            window.addEventListener('message', this.#fionaListener);
        }

        #isObservedEvent(eventName) {
            return [
                'clientTagsLoaded',
                'appComplete',
                'depositsOfferClick',
                'loanOfferClick',
                'specialOfferClick',
            ].includes(eventName);
        }
    }

    window.customElements.define('phoenix-even-financial', PhoenixEvenFinancial);
});
